import axios from "axios";
import { DEFAULT_AYANAMSA } from "../constants/jyotish";
import i18n from "../i18n";
import * as offlineCache from "./offlineCache";
import { PREFS_OWNER_STORAGE_KEY } from "../config/prefsOwner";

// Endpoints whose *data* PyJHora can localize (yoga/raja-yoga names + descriptions)
// take a `lang`. Read at call time so it follows the switcher without a reload.
const uiLang = () => i18n.language || "en";

// Base URL for the backend API. Prefer an explicit REACT_APP_API_URL, but when
// it's unset default to the SAME host the page was served from (on port 8000).
// That way the app works from any device on the LAN with no per-device config:
// desktop via localhost, a phone via the machine's hostname/IP, etc. — the API
// host always matches the page host, so it can't drift to localhost on a phone.
const deriveApiUrl = () => {
  if (typeof window !== "undefined" && window.location) {
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:8000`;
  }
  return "http://localhost:8000";
};

// REACT_APP_API_URL semantics:
//   unset              -> same host, port 8000 (LAN self-host; see deriveApiUrl above)
//   "" (empty string)  -> same ORIGIN, relative paths ("/api/..."). For reverse-proxy
//                         deploys where a single hostname fronts both the app and the
//                         API (nginx + Cloudflare Tunnel): no port, no cross-origin, no
//                         CORS. This is what the NAS build (Dockerfile.nas) bakes in.
//   "http://host:port" -> pinned absolute backend URL
const rawApiUrl = process.env.REACT_APP_API_URL;
export const API_URL = rawApiUrl === undefined ? deriveApiUrl() : rawApiUrl;

if (rawApiUrl === undefined) {
  // eslint-disable-next-line no-console
  console.info(`[config] REACT_APP_API_URL unset; using same-host default ${API_URL}.`);
}

const api = axios.create({
  baseURL: API_URL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || "30000"),
  headers: {
    "Content-Type": "application/json",
  },
});

// Token storage helpers. Both the short-lived access token and the long-lived
// refresh token live in localStorage (v1 — same as before; a refresh token is
// what keeps you signed in across access-token expiry). Known XSS tradeoff vs
// httpOnly cookies; acceptable given the app already used localStorage bearers.
const ACCESS_KEY = "access_token";
const REFRESH_KEY = "refresh_token";

export const setTokens = ({ access_token, refresh_token } = {}) => {
  if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
};
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  // Saved computations are per-user; this browser may belong to a household.
  offlineCache.clearAll();
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Birth-chart-bound AI endpoints whose saved readings should be grouped under the
// currently-selected profile in the unified history. We inject `profile_id` from
// localStorage so every reading page doesn't have to thread it through by hand.
// (Location-driven tools — Muhurta / Prashna / Almanac — are intentionally absent
// so their readings land in the "No profile" bucket. `ask` sends its own id.)
const PROFILE_READING_PATHS = new Set([
  "/api/astrology/varshaphal-analysis",
  "/api/astrology/life-timeline-analysis",
  "/api/astrology/planet-conditions-analysis",
  "/api/astrology/avasthas-analysis",
  "/api/astrology/strength-analysis",
  "/api/astrology/saturn-transits-analysis",
  "/api/astrology/nakshatra-profile-analysis",
  "/api/astrology/gochara-phala-analysis",
  "/api/astrology/nakshatra-gochara-analysis",
  "/api/astrology/friendships-analysis",
  "/api/astrology/nadi-analysis",
  "/api/astrology/bhrigu-markers-analysis",
  "/api/astrology/remedies-analysis",
  "/api/astrology/daily-digest-analysis",
  "/api/astrology/fortnightly-digest-analysis",
  "/api/astrology/monthly-digest-analysis",
  "/api/astrology/tithi-pravesha-analysis",
  "/api/astrology/sensitive-points-analysis",
  "/api/astrology/celestial-analysis",
  "/api/astrology/pancha-pakshi-analysis",
  "/api/astrology/sarvatobhadra-analysis",
  "/api/astrology/compatibility-analysis",
  "/api/astrology/compare-analysis",
  "/api/astrology/rectify-birth-time/explain",
  "/api/astrology/rectify-birth-time/events/explain",
  "/api/astrology/predict",
]);

api.interceptors.request.use((config) => {
  if (
    (config.method || "").toLowerCase() === "post" &&
    PROFILE_READING_PATHS.has(config.url) &&
    config.data &&
    typeof config.data === "object" &&
    config.data.profile_id == null
  ) {
    try {
      const p = JSON.parse(localStorage.getItem("selectedProfile") || "null");
      if (p && p._id) config.data = { ...config.data, profile_id: p._id };
    } catch (e) {
      /* no selected profile — reading saved without one */
    }
  }
  return config;
});

// --- Silent refresh on 401 -------------------------------------------------
// When a call 401s (access token expired), transparently exchange the refresh
// token for a fresh pair and retry the original request ONCE — so the user
// isn't bounced to /login every ACCESS_TOKEN_EXPIRE_MINUTES. A single in-flight
// refresh is shared across concurrent 401s. Only a failed refresh logs out.
let refreshPromise = null;

const doRefresh = async () => {
  const refresh_token = getRefreshToken();
  if (!refresh_token) throw new Error("no refresh token");
  // Bare axios (not `api`) so this request skips the interceptors below and
  // can't recurse into another refresh attempt.
  const resp = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token });
  setTokens(resp.data);
  return resp.data.access_token;
};

// --- Offline readability (§68.8) -------------------------------------------
// Every deterministic computation is kept in IndexedDB as it comes back, and
// replayed from there when the network is gone — so opening the app on a plane
// or in a metro shows the chart you were last looking at instead of an empty
// shell. See services/offlineCache.js for what is cached and what never is.
//
// The key is namespaced by user so two people sharing a browser cannot read
// each other's charts; `clearTokens` drops the store on logout.
const cacheOwner = () => {
  try {
    // The same stamp SettingsContext trusts for "whose cached state is this" —
    // see config/prefsOwner.js.
    return localStorage.getItem(PREFS_OWNER_STORAGE_KEY) || "anon";
  } catch (e) {
    return "anon";
  }
};

// --- Detached AI requests ----------------------------------------------------
// Every "AI reading" call (the *-analysis panels, predict, quiz, rectification…)
// asks the server to run it detached (backend/detached_http.py): the POST comes
// straight back 202 {detached_job}, and we long-poll /api/ai/jobs/{id} (≤25 s a
// poll) until the handler's own response is ready — which we then hand to the
// caller as if the POST had returned it. No single connection lasts long enough
// for Cloudflare's ~100 s cut, a network blip just means another poll, and a job
// id kept in localStorage lets an identical request made after iOS killed the
// tab pick up the same reading instead of starting over. Callers don't change.
const DETACHABLE_AI =
  /^\/api\/astrology\/([a-z0-9-]+-analysis|predict|ask|quiz\/generate|quiz\/grade|rectify-birth-time\/(chat|explain|events\/explain)|life-report\/chapter)$/;
export const AI_READING_SAVED_EVENT = "jyotir:ai-reading-saved";
const AI_PENDING_KEY = "ai_pending_jobs";
const AI_PENDING_MAX_AGE_MS = 6 * 60 * 60 * 1000; // the server keeps jobs no longer
const AI_POLL_RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000, 15000, 30000, 30000];

const aiPath = (url = "") => {
  const path = url.split("?")[0];
  return path.startsWith(API_URL) ? path.slice(API_URL.length) : path;
};

// A short, stable fingerprint of the request (method, path, params, body).
const aiRequestKey = (config) => {
  const str = offlineCache.cacheKey({ ...config, url: aiPath(config.url) }, cacheOwner());
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

const readAiPending = () => {
  try {
    const all = JSON.parse(localStorage.getItem(AI_PENDING_KEY) || "{}");
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(all).filter(([, v]) => v && now - v.at < AI_PENDING_MAX_AGE_MS)
    );
  } catch (e) {
    return {};
  }
};
const writeAiPending = (key, jobId) => {
  try {
    const all = readAiPending();
    if (jobId) all[key] = { jobId, at: Date.now() };
    else delete all[key];
    localStorage.setItem(AI_PENDING_KEY, JSON.stringify(all));
  } catch (e) {
    /* storage blocked: the reading still arrives, it just can't survive a reload */
  }
};

// Tell the header's activity pill a job just started, so it shows at once
// instead of on its next poll (hooks/useAiActivity.js listens).
const announceAiJob = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("jyotir:ai-activity"));
};

const settleAs = (resp, config) => {
  const final = {
    data: resp.data,
    status: resp.status,
    statusText: resp.statusText,
    headers: resp.headers,
    config,
    request: resp.request,
  };
  if (final.status >= 200 && final.status < 300) return final;
  const err = new Error(`Request failed with status code ${final.status}`);
  err.name = "AxiosError";
  err.isAxiosError = true;
  err.config = config;
  err.response = final;
  throw err;
};

// Poll a detached job to its end and return what the original POST would have.
const followAiJob = async (jobId, config, key) => {
  const signal = config.signal || new AbortController().signal;
  let attempt = 0;
  while (true) {
    let resp;
    try {
      resp = await api.get(`/api/ai/jobs/${encodeURIComponent(jobId)}`, {
        timeout: 60000,
        signal: config.signal,
        // Hand the handler's own errors back verbatim; only a 401 goes through
        // the refresh interceptor (a phone back after an hour has a stale token).
        validateStatus: (s) => s !== 401,
      });
    } catch (err) {
      if (err.response || signal.aborted || axios.isCancel(err)) throw err;
      // No response: the network dropped. Wait it out — the job keeps running.
      if (attempt >= AI_POLL_RETRY_DELAYS.length) throw err;
      await sleep(AI_POLL_RETRY_DELAYS[attempt++], signal);
      await untilReachable(signal);
      continue;
    }
    attempt = 0;
    if (resp.status === 202 && resp.data?.status === "running") continue;
    if (key) writeAiPending(key, null);
    if (resp.status === 404 && resp.data?.detail === "Job not found") {
      // Gone (server restarted, or finished hours ago): ask again, for real.
      // eslint-disable-next-line no-unused-vars
      const { adapter, _aiKey, ...fresh } = config;
      return api.request({ ...fresh, _aiResumeFailed: true });
    }
    return settleAs(resp, config);
  }
};

api.interceptors.request.use((config) => {
  if ((config.method || "").toLowerCase() !== "post" || !DETACHABLE_AI.test(aiPath(config.url))) {
    return config;
  }
  config.headers = config.headers || {};
  if (typeof config.headers.set === "function") config.headers.set("X-Detach", "1");
  else config.headers["X-Detach"] = "1";
  // Where the reader is, so the activity list can link a finished job back.
  if (typeof window !== "undefined") {
    const page = window.location.pathname;
    if (typeof config.headers.set === "function") config.headers.set("X-AI-Page", page);
    else config.headers["X-AI-Page"] = page;
  }
  const key = aiRequestKey(config);
  config._aiKey = key;
  const pending = !config._aiResumeFailed && readAiPending()[key];
  // The same reading was already asked for and never collected (the tab was
  // killed mid-wait): collect it instead of asking the model all over again.
  if (pending) config.adapter = (cfg) => followAiJob(pending.jobId, cfg, key);
  return config;
});

api.interceptors.response.use((response) => {
  const jobId = response.status === 202 && response.data?.detached_job;
  if (!jobId) return response;
  const key = response.config?._aiKey;
  if (key) writeAiPending(key, jobId);
  announceAiJob();
  return followAiJob(jobId, response.config, key);
});

api.interceptors.response.use((response) => {
  const config = response.config || {};
  // An AI reading's handler saves it to history before it answers, so a page's
  // "Recent readings" strip can reload now (components/RecentReadings.js listens).
  if (
    response.status === 200 &&
    (config.method || "").toLowerCase() === "post" &&
    DETACHABLE_AI.test(aiPath(config.url)) &&
    typeof window !== "undefined"
  ) {
    window.dispatchEvent(new Event(AI_READING_SAVED_EVENT));
  }
  if (response.status === 200 && offlineCache.isCacheable(config.method, config.url)) {
    // Fire and forget: a full disk must never fail a request that succeeded.
    offlineCache.put(offlineCache.cacheKey(config, cacheOwner()), response.data);
  }
  return response;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const isAuthCall = (original.url || "").includes("/api/auth/");

    // No response at all means the network failed (not the server saying no),
    // so a saved copy is the honest answer rather than an error page.
    if (!error.response && offlineCache.isCacheable(original.method, original.url)) {
      const hit = await offlineCache.get(offlineCache.cacheKey(original, cacheOwner()));
      if (hit) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("jyotir:served-offline", { detail: { savedAt: hit.savedAt } })
          );
        }
        return {
          data: hit.data,
          status: 200,
          statusText: "OK (offline copy)",
          headers: {},
          config: original,
          fromOfflineCache: true,
          savedAt: hit.savedAt,
        };
      }
    }

    if (status !== 401 || isAuthCall) {
      return Promise.reject(error);
    }

    if (!original._retried) {
      original._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = doRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccess = await refreshPromise;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newAccess}` };
        return api(original);
      } catch (e) {
        // Refresh failed — fall through to a hard logout.
      }
    }

    clearTokens();
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authService = {
  register: (username, email, password, name, rememberMe = false) =>
    api.post("/api/auth/register", { username, email, password, name, remember_me: rememberMe }),
  login: (username, password, rememberMe = false) =>
    api.post("/api/auth/login", { username, password, remember_me: rememberMe }),
  // Sign in with Google: `credential` is the ID token from Google Identity Services.
  googleLogin: (credential, rememberMe = false) =>
    api.post("/api/auth/google", { credential, remember_me: rememberMe }),
  refresh: (refresh_token) => api.post("/api/auth/refresh", { refresh_token }),
  logout: (refresh_token) => api.post("/api/auth/logout", { refresh_token }),
  changePassword: (currentPassword, newPassword) =>
    api.post("/api/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  updateEmail: (email) => api.put("/api/auth/email", { email }),
  updateName: (name) => api.put("/api/auth/name", { name }),
  logoutOtherDevices: () => api.post("/api/auth/logout-all"),
  deleteAccount: (password) => api.delete("/api/auth/account", { data: { password } }),
  getProfile: () => api.get("/api/user/profile"),
  // Forgot / reset password (email link). `identifier` = username or email.
  forgotPassword: (identifier) => api.post("/api/auth/forgot-password", { identifier }),
  resetPassword: (token, newPassword) =>
    api.post("/api/auth/reset-password", { token, new_password: newPassword }),
  // Public-API / MCP tokens (§2.3). createApiToken returns the raw token ONCE.
  listApiTokens: () => api.get("/api/auth/api-tokens"),
  createApiToken: (label) => api.post("/api/auth/api-tokens", { label }),
  revokeApiToken: (id) => api.delete(`/api/auth/api-tokens/${id}`),
};

// Admin console (§44). Deployer-only; every call is gated server-side by the
// ADMIN_USERNAMES allowlist. `me` is the only one a non-admin may call.
export const adminService = {
  me: () => api.get("/api/admin/me"),
  stats: () => api.get("/api/admin/stats"),
  listUsers: (q = "", limit = 200) => api.get("/api/admin/users", { params: { q, limit } }),
  userDetail: (username) => api.get(`/api/admin/users/${encodeURIComponent(username)}`),
  userContent: (username, kind) =>
    api.get(`/api/admin/users/${encodeURIComponent(username)}/content/${kind}`),
  suspend: (username, suspended) =>
    api.post(`/api/admin/users/${encodeURIComponent(username)}/suspend`, { suspended }),
  deleteUser: (username) => api.delete(`/api/admin/users/${encodeURIComponent(username)}`),
  // The audit log records *events* (moderation + security). Filters are all
  // optional; empty strings are dropped server-side.
  audit: (params = {}) => api.get("/api/admin/audit", { params: { limit: 200, ...params } }),
  // The activity feed is derived on read from existing collections, so it covers
  // everything that ever happened — not only what has been logged since logging
  // was added. That distinction is why these are two separate views.
  activity: (params = {}) => api.get("/api/admin/activity", { params: { limit: 200, ...params } }),
  getConfig: () => api.get("/api/admin/config"),
  setConfig: (updates) => api.put("/api/admin/config", updates),
  // Claim checks (§68.1): how often a reading contradicted the chart it was
  // generated from, and the queue of the ones that still did after the retry.
  claimSummary: (days = 30) => api.get("/api/admin/claim-checks/summary", { params: { days } }),
  claimChecks: (params = {}) =>
    api.get("/api/admin/claim-checks", { params: { limit: 100, ...params } }),
  triageClaim: (id, patch) => api.patch(`/api/admin/claim-checks/${id}`, patch),
};

// Daily-digest notification preferences + Web Push (§16).
export const notificationsService = {
  getPrefs: () => api.get("/api/notifications/prefs"),
  setPrefs: (prefs) => api.put("/api/notifications/prefs", prefs),
  subscribePush: (subscription) => api.post("/api/notifications/push/subscribe", { subscription }),
  unsubscribePush: (endpoint) => api.post("/api/notifications/push/unsubscribe", { endpoint }),
  sendDigestNow: (cadence = "daily") =>
    api.post("/api/notifications/digest/send", null, { params: { cadence } }),
  // The chart's forward calendar (§70) — what the alerts are sent from, and what
  // the Life Timeline's "Upcoming" tab reads.
  events: (params = {}) => api.get("/api/notifications/events", { params }),
  sendEventAlertsNow: () => api.post("/api/notifications/events/send"),
  // Public recipient opt-in / opt-out, reached from an emailed link (no auth).
  confirmDigest: (token) => api.get("/api/digest/confirm", { params: { token } }),
  unsubscribeDigest: (token) => api.get("/api/digest/unsubscribe", { params: { token } }),
};

export const astrologyService = {
  calculateBirthChart: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/birth-chart", birthDetails, { params: { ayanamsa } }),
  getBirthChart: (chartId) => api.get(`/api/astrology/birth-chart/${chartId}`),
  getDivisionalChart: (birthDetails, varga = 9, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/divisional-chart", birthDetails, {
      params: { varga, ayanamsa },
    }),
  getHoroscope: (birthDetails) => api.post("/api/astrology/horoscope", birthDetails),
  getPanchanga: ({ place, latitude, longitude, timezone, date, system } = {}) =>
    api.get("/api/astrology/panchanga", {
      params: { place, latitude, longitude, timezone, date, system },
    }),
  // Almanac (§9.2): planetary hours, eclipses, festival/vratha dates.
  getPlanetaryHours: ({ place, latitude, longitude, timezone, date } = {}) =>
    api.get("/api/astrology/almanac/hora", {
      params: { place, latitude, longitude, timezone, date },
    }),
  getEclipses: ({ place, latitude, longitude, timezone, fromDate, count } = {}) =>
    api.get("/api/astrology/almanac/eclipses", {
      params: { place, latitude, longitude, timezone, from_date: fromDate, count },
    }),
  getFestivals: ({ place, latitude, longitude, timezone, start, end, types } = {}) =>
    api.get("/api/astrology/almanac/festivals", {
      params: {
        place,
        latitude,
        longitude,
        timezone,
        start,
        end,
        types: Array.isArray(types) ? types.join(",") : types,
      },
    }),
  getConjunctions: ({ place, latitude, longitude, timezone, start, end, maxSep } = {}) =>
    api.get("/api/astrology/almanac/conjunctions", {
      params: { place, latitude, longitude, timezone, start, end, max_sep: maxSep },
    }),
  // Plain-language AI day-guide from the almanac (panchanga + hora), location-driven.
  analyzeAlmanacAI: ({ place, latitude, longitude, timezone, date, system } = {}, model = {}) =>
    api.post(
      "/api/astrology/almanac-analysis",
      {
        place,
        latitude,
        longitude,
        timezone,
        date,
        system,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
      },
      { timeout: 300000 }
    ),
  getDoshas: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/doshas", birthDetails, { params: { ayanamsa, lang: uiLang() } }),
  getYogas: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/yogas", birthDetails, { params: { ayanamsa, lang: uiLang() } }),
  // The ayanamsa is load-bearing: a nakshatra dasha's balance at birth is read
  // off the Moon's sidereal longitude, so Lahiri vs True Chitra moves the whole
  // timeline ~3 days — enough to change the running lord at Sookshma.
  getDhasa: (birthDetails, dashaType = "vimsottari", ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/dhasa", birthDetails, {
      params: { dhasa_type: dashaType, ayanamsa },
    }),
  // Lazily fetch the immediate children of a Vimsottari node. `lordsPath` is the
  // chain of lord names from the Maha Dasha down, e.g. ["Venus", "Saturn"].
  getDhasaChildren: (birthDetails, lordsPath = [], ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/dhasa/children", birthDetails, {
      params: { lords: lordsPath.join(","), ayanamsa },
    }),
  getTransits: (
    birthDetails,
    currentDate = null,
    ayanamsa = DEFAULT_AYANAMSA,
    currentTime = null,
    currentTz = null
  ) =>
    api.post("/api/astrology/transit", birthDetails, {
      params: {
        current_date: currentDate,
        current_time: currentTime,
        current_tz: currentTz,
        ayanamsa,
      },
    }),
  // Bhava (house-cusp) chart — Bhava Chalit / cuspal division (Sripati/Placidus/KP/Equal).
  getBhavaChart: (birthDetails, method = "SRIPATI", ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/bhava-chart", birthDetails, {
      params: { method, ayanamsa },
    }),
  // Sidereal ephemeris + ingress calendar over a date window.
  getEphemeris: ({
    startDate,
    days = 30,
    place,
    latitude,
    longitude,
    timezone,
    ayanamsa = DEFAULT_AYANAMSA,
  } = {}) =>
    api.get("/api/astrology/ephemeris", {
      params: {
        start_date: startDate,
        days,
        place,
        latitude,
        longitude,
        timezone,
        ayanamsa,
      },
    }),
  // Other (non-Vimsottari) dasha systems.
  getDashaSystems: () => api.get("/api/astrology/dasha-systems"),
  // The ayanamsa matters here: a nakshatra dasha's balance at birth comes
  // straight off the Moon's sidereal longitude.
  getDashaPeriods: (birthDetails, dhasaType, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/dasha-periods", birthDetails, {
      params: { dhasa_type: dhasaType, ayanamsa },
    }),
  // Ashtakavarga (Bhinna + Sarva).
  getAshtakavarga: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/ashtakavarga", birthDetails, { params: { ayanamsa } }),
  // Advanced chart details: arudha padas, karakas, special lagnas, upagrahas.
  getChartDetails: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/chart-details", birthDetails, { params: { ayanamsa } }),
  // Shadbala / planetary strength.
  getShadbala: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/shadbala", birthDetails, { params: { ayanamsa } }),
  // Graha drishti (aspects): per-graha houses/planets aspected + strength %.
  getAspects: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/aspects", birthDetails, { params: { ayanamsa } }),
  // Dedicated Raja Yogas (Kendra-Trikona pairs + named special types).
  getRajaYogas: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/raja-yogas", birthDetails, { params: { ayanamsa, lang: uiLang() } }),
  // Ayu (longevity) category — Alpa/Madhya/Purna + factors.
  getLongevity: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/longevity", birthDetails, { params: { ayanamsa } }),
  // Sensitive points (§11.1): Sphutas + 36 Sahams + Argala, aggregated.
  getSensitivePoints: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/sensitive-points", birthDetails, { params: { ayanamsa } }),
  getSpecialPoints: (birthDetails, ayanamsa = DEFAULT_AYANAMSA, varnadaMethod = 1) =>
    api.post("/api/astrology/special-points", birthDetails, {
      params: { ayanamsa, varnada_method: parseInt(varnadaMethod, 10) || 1 },
    }),
  analyzeSensitivePointsAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/sensitive-points-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // Vedic clock & retrograde (§11.2). Location-driven (not birth-chart bound).
  getVedicClock: ({ place, latitude, longitude, timezone, date } = {}) =>
    api.post("/api/astrology/vedic-clock", null, {
      params: { place, latitude, longitude, timezone, date },
    }),
  getRetrograde: ({ place, latitude, longitude, timezone, date } = {}) =>
    api.post("/api/astrology/retrograde", null, {
      params: { place, latitude, longitude, timezone, date },
    }),
  analyzeCelestialAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/celestial-analysis",
      {
        birth_details: birthDetails,
        date: opts.date,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // Sudarsana Chakra — three wheels (Lagna/Moon/Sun) for a solar-return year.
  getSudarsanaChakra: (birthDetails, yearOffset = 0, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/sudarsana-chakra", birthDetails, {
      params: { year_offset: yearOffset, ayanamsa },
    }),
  // Pancha Pakshi Sastra — birth bird + day's activity-strength timeline.
  // The bird comes from the birth nakshatra, so the ayanamsa can change which
  // bird you are, not just the timings.
  getPanchaPakshi: (birthDetails, date = null, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/pancha-pakshi", birthDetails, {
      params: { ...(date ? { date } : {}), ayanamsa },
    }),
  // Plain-language AI reading of today's Pancha Pakshi timing.
  analyzePanchaPakshiAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/pancha-pakshi-analysis",
      {
        birth_details: birthDetails,
        date: opts.date,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // Varshaphal / Tajaka annual (solar-return) horoscope for a target year.
  getVarshaphal: (birthDetails, year, ayanamsa = DEFAULT_AYANAMSA, dashaSystem = "mudda") =>
    api.post("/api/astrology/varshaphal", birthDetails, {
      params: { year, ayanamsa, dasha_system: dashaSystem },
    }),
  // Plain-language AI year-ahead reading of the Varshaphal chart.
  analyzeVarshaphalAI: (birthDetails, year, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/varshaphal-analysis",
      {
        birth_details: birthDetails,
        year,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // EXPERIMENTAL birth-time rectification (BV Raman suddhi methods).
  rectifyBirthTime: (
    birthDetails,
    method = "nakshatra",
    gender = null,
    ayanamsa = DEFAULT_AYANAMSA
  ) =>
    api.post("/api/astrology/rectify-birth-time", birthDetails, {
      params: { method, ...(gender != null ? { gender } : {}), ayanamsa },
    }),
  // Plain-language AI note on why the suggested (rectified) time fits better.
  explainRectificationAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/rectify-birth-time/explain",
      {
        birth_details: birthDetails,
        method: opts.method || "nakshatra",
        gender: opts.gender,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // EXPERIMENTAL event-based rectification — scans candidate times for the best
  // dasha/transit fit to the supplied dated life events.
  rectifyByEvents: (birthDetails, events, windowMinutes = 120, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post(
      "/api/astrology/rectify-birth-time/events",
      {
        birth_details: birthDetails,
        events,
        window_minutes: windowMinutes,
        ayanamsa,
      },
      { timeout: 300000 }
    ),
  // Plain-language AI note on why the event-matched time fits the events.
  explainEventRectificationAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/rectify-birth-time/events/explain",
      {
        birth_details: birthDetails,
        events: opts.events || [],
        window_minutes: opts.windowMinutes || 120,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // Conversational rectification: one interview turn → { reply, events, ready }.
  rectifyChat: (birthDetails, messages, collectedEvents = [], opts = {}, model = {}) =>
    api.post(
      "/api/astrology/rectify-birth-time/chat",
      {
        birth_details: birthDetails,
        messages,
        collected_events: collectedEvents,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),
  // Shareable read-only chart link.
  createShare: (birthDetails, ayanamsa = DEFAULT_AYANAMSA, profileName = null) =>
    api.post("/api/astrology/share", {
      birth_details: birthDetails,
      ayanamsa,
      profile_name: profileName,
    }),
  getSharedChart: (token) => api.get(`/api/astrology/share/${token}`),
  getCompatibility: (maleBirthDetails, femaleBirthDetails) =>
    api.post("/api/astrology/compatibility", {
      male_dob: maleBirthDetails.dob,
      male_tob: maleBirthDetails.tob,
      male_place: maleBirthDetails.place,
      male_latitude: maleBirthDetails.latitude,
      male_longitude: maleBirthDetails.longitude,
      male_timezone: maleBirthDetails.timezone,
      female_dob: femaleBirthDetails.dob,
      female_tob: femaleBirthDetails.tob,
      female_place: femaleBirthDetails.place,
      female_latitude: femaleBirthDetails.latitude,
      female_longitude: femaleBirthDetails.longitude,
      female_timezone: femaleBirthDetails.timezone,
    }),
  // 7th-house marriage workspace for a couple (§2.6).
  getMarriageWorkspace: (maleBirthDetails, femaleBirthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/marriage-workspace", {
      male_details: maleBirthDetails,
      female_details: femaleBirthDetails,
      ayanamsa,
    }),
  getUserCharts: () => api.get("/api/user/charts"),

  // ---- Muhurta / electional astrology (§16) ----
  // Location-driven: find auspicious windows for an activity over a date range.
  // `birthDetails` is what turns an almanac search into this person's search
  // (§68.6) — Tara Bala, Chandra Bala, the running dasha lords and per-window
  // lagna shuddhi. Omit it and the endpoint answers exactly as it always did.
  getMuhurta: ({
    activity,
    startDate,
    endDate,
    place,
    latitude,
    longitude,
    timezone,
    birthDetails,
    ayanamsa = DEFAULT_AYANAMSA,
  } = {}) =>
    api.post("/api/astrology/muhurta", birthDetails || null, {
      params: {
        activity,
        start_date: startDate,
        end_date: endDate,
        place,
        latitude,
        longitude,
        timezone,
        ayanamsa,
      },
    }),
  analyzeMuhurtaAI: (
    {
      activity,
      startDate,
      endDate,
      place,
      latitude,
      longitude,
      timezone,
      birthDetails,
      profileId,
      personName,
      ayanamsa = DEFAULT_AYANAMSA,
    } = {},
    model = {}
  ) =>
    api.post(
      "/api/astrology/muhurta-analysis",
      {
        activity,
        start_date: startDate,
        end_date: endDate,
        place,
        latitude,
        longitude,
        timezone,
        birth_details: birthDetails || undefined,
        profile_id: profileId,
        person_name: personName,
        ayanamsa,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
      },
      { timeout: 300000 }
    ),

  // ---- Prashna / horary (§16) ----
  // Cast a chart for the moment (defaults to now + here) and read it. The
  // -analysis endpoint returns both the reading and the chart.
  getPrashna: ({ question, date, time, place, latitude, longitude, timezone, ayanamsa } = {}) =>
    api.post("/api/astrology/prashna", null, {
      params: { question, date, time, place, latitude, longitude, timezone, ayanamsa },
    }),
  analyzePrashnaAI: (
    { question, date, time, place, latitude, longitude, timezone } = {},
    model = {}
  ) =>
    api.post(
      "/api/astrology/prashna-analysis",
      {
        question,
        date,
        time,
        place,
        latitude,
        longitude,
        timezone,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Daily digest (§16) ----
  getDailyDigest: (
    birthDetails,
    { date, currentTime, currentTz, basis, ayanamsa = DEFAULT_AYANAMSA } = {}
  ) =>
    api.post("/api/astrology/daily-digest", birthDetails, {
      params: { date, current_time: currentTime, current_tz: currentTz, basis, ayanamsa },
    }),
  analyzeDailyDigestAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/daily-digest-analysis",
      {
        birth_details: birthDetails,
        date: opts.date,
        current_time: opts.currentTime,
        current_tz: opts.currentTz,
        basis: opts.basis,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Fortnightly & monthly digests (§25) ----
  // `basis` selects the pravesha ladder: "solar" (Tajaka: Maasa Pravesha) or
  // "lunar" (tithi: birth-tithi return). The fortnight is lunar-only (there is no
  // solar fortnight rung), so it takes no basis.
  getFortnightlyDigest: (birthDetails, { date, ayanamsa = DEFAULT_AYANAMSA } = {}) =>
    api.post("/api/astrology/fortnightly-digest", birthDetails, { params: { date, ayanamsa } }),
  getMonthlyDigest: (birthDetails, { date, basis = "solar", ayanamsa = DEFAULT_AYANAMSA } = {}) =>
    api.post("/api/astrology/monthly-digest", birthDetails, { params: { date, basis, ayanamsa } }),
  analyzePeriodDigestAI: (period, birthDetails, opts = {}, model = {}) =>
    api.post(
      `/api/astrology/${period}-digest-analysis`,
      {
        birth_details: birthDetails,
        date: opts.date,
        basis: opts.basis,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Tithi Pravesha — the *lunar*-return chart, on any rung of the ladder ----
  getTithiPravesha: (birthDetails, { year, date, ayanamsa = DEFAULT_AYANAMSA } = {}) =>
    api.post("/api/astrology/tithi-pravesha", birthDetails, { params: { year, date, ayanamsa } }),
  /** Any rung of the lunar pravesha ladder: "tithi" (~1d), "paksha" (~14.8d),
   *  "month" (~29.5d) or "annual" (the TP chart proper). `year` targets an annual
   *  window; `date` selects whichever window contains it — which is how the ±
   *  stepper walks the ladder. */
  getLunarPravesha: (
    birthDetails,
    { rung = "annual", year, date, ayanamsa = DEFAULT_AYANAMSA } = {}
  ) =>
    api.post("/api/astrology/lunar-pravesha", birthDetails, {
      params: { rung, year, date, ayanamsa },
    }),
  /** One level of the compressed Tithi Ashtottari tree, fetched when a period is
   *  expanded. `period` is a row from the table: {start_jd, lord, span_deg, level}
   *  plus the place — that is all a subdivision needs. */
  getTithiAshtottariChildren: (period) =>
    api.post("/api/astrology/tithi-ashtottari-children", period),
  analyzeTithiPraveshaAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/tithi-pravesha-analysis",
      {
        birth_details: birthDetails,
        rung: opts.rung,
        year: opts.year,
        date: opts.date,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Muhurta sub-tools: Choghadiya / Panchaka / Tarabala / Chandrabala ----
  // Location-driven; pass birthDetails to personalize Tarabala + Chandrabala.
  getMuhurtaSubtools: ({
    date,
    place,
    latitude,
    longitude,
    timezone,
    birthDetails,
    ayanamsa = DEFAULT_AYANAMSA,
  } = {}) =>
    api.post("/api/astrology/muhurta/subtools", {
      date,
      place,
      latitude,
      longitude,
      timezone,
      birth_details: birthDetails || undefined,
      ayanamsa,
    }),

  // ---- Nadi karaka reading (significators + transit triggers) ----
  getNadiReading: (birthDetails, { gender, ayanamsa = DEFAULT_AYANAMSA } = {}) =>
    api.post("/api/astrology/nadi", birthDetails, {
      params: { gender, ayanamsa },
    }),
  analyzeNadiReadingAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/nadi-analysis",
      {
        birth_details: birthDetails,
        gender: opts.gender,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Nadi / Bhrigu-style yearly markers ----
  getBhriguMarkers: (birthDetails, { fromAge, years = 12, ayanamsa = DEFAULT_AYANAMSA } = {}) =>
    api.post("/api/astrology/bhrigu-markers", birthDetails, {
      params: { from_age: fromAge, years, ayanamsa },
    }),
  analyzeBhriguMarkersAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/bhrigu-markers-analysis",
      {
        birth_details: birthDetails,
        from_age: opts.fromAge,
        years: opts.years || 12,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Life timeline (dasha bands + Sade Sati + ingresses + eclipses) ----
  getLifeTimeline: (
    birthDetails,
    { yearsBefore = 10, yearsAfter = 10, ayanamsa = DEFAULT_AYANAMSA } = {}
  ) =>
    api.post("/api/astrology/life-timeline", birthDetails, {
      params: { years_before: yearsBefore, years_after: yearsAfter, ayanamsa },
    }),
  analyzeLifeTimelineAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/life-timeline-analysis",
      {
        birth_details: birthDetails,
        target_date: opts.targetDate,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Planet conditions (combustion / vargottama / gandanta / …) ----
  getPlanetConditions: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/planet-conditions", birthDetails, { params: { ayanamsa } }),
  analyzePlanetConditionsAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/planet-conditions-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Sade Sati & Saturn transits ----
  getSaturnTransits: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/saturn-transits", birthDetails, { params: { ayanamsa } }),
  analyzeSaturnTransitsAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/saturn-transits-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Nakshatra (birth star) profile + tarabala calendar ----
  getNakshatraProfile: (birthDetails, currentDate = null, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/nakshatra-profile", birthDetails, {
      params: { current_date: currentDate || undefined, ayanamsa },
    }),
  analyzeNakshatraProfileAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/nakshatra-profile-analysis",
      {
        birth_details: birthDetails,
        profile_id: opts.profileId,
        person_name: opts.personName,
        current_date: opts.currentDate,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // Reading of the star each graha occupies — distinct from the janma-star
  // profile above, which only ever reads the Moon's nakshatra.
  analyzePlanetaryNakshatrasAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/planetary-nakshatras-analysis",
      {
        birth_details: birthDetails,
        profile_id: opts.profileId,
        person_name: opts.personName,
        current_date: opts.currentDate,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Gochara-phala (Moon-referenced transits with vedha) ----
  getGocharaPhala: (
    birthDetails,
    currentDate = null,
    currentTz = null,
    ayanamsa = DEFAULT_AYANAMSA
  ) =>
    api.post("/api/astrology/gochara-phala", birthDetails, {
      params: {
        current_date: currentDate || undefined,
        current_tz: currentTz ?? undefined,
        ayanamsa,
      },
    }),
  analyzeGocharaPhalaAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/gochara-phala-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        current_date: opts.currentDate,
        current_tz: opts.currentTz,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Nakshatra gochara (transit stars + dated windows) — §75 ----
  getNakshatraGochara: (
    birthDetails,
    currentDate = null,
    currentTime = null,
    currentTz = null,
    horizonDays = null,
    ayanamsa = DEFAULT_AYANAMSA
  ) =>
    api.post("/api/astrology/nakshatra-gochara", birthDetails, {
      params: {
        current_date: currentDate || undefined,
        current_time: currentTime || undefined,
        current_tz: currentTz ?? undefined,
        horizon_days: horizonDays || undefined,
        ayanamsa,
      },
    }),
  analyzeNakshatraGocharaAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/nakshatra-gochara-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        profile_id: opts.profileId,
        current_date: opts.currentDate,
        current_time: opts.currentTime,
        current_tz: opts.currentTz,
        horizon_days: opts.horizonDays,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Classical-text RAG (citations) status ----
  getAiSources: () => api.get("/api/ai/sources"),

  // ---- Composed Life Report (multi-chapter) ----
  getLifeReportChapters: () => api.get("/api/astrology/life-report/chapters"),
  // The per-chapter and save endpoints still exist on the API, but the app no
  // longer drives generation from the browser — see startLifeReport below.

  // Server-side Life Report run. The browser only starts it and polls: generation
  // continues on the server even while the phone is asleep or the tab is closed.
  startLifeReport: (birthDetails, opts = {}, model = {}) =>
    api.post("/api/astrology/life-report/start", {
      birth_details: birthDetails,
      person_name: opts.personName,
      profile_id: opts.profileId,
      regenerate: opts.regenerate || false,
      llm_provider: model.legacyProvider || "qwen",
      provider_type: model.providerType,
      model: model.model,
      base_url: model.baseUrl,
      api_key: model.apiKey,
      max_tokens: model.maxTokens || undefined,
      ayanamsa: model.ayanamsa,
    }),
  getLifeReportJob: (profileId) =>
    api.get("/api/astrology/life-report/job", { params: { profile_id: profileId } }),
  cancelLifeReport: (jobId) =>
    api.post("/api/astrology/life-report/cancel", null, { params: { job_id: jobId } }),

  // ---- iCal calendar subscription ----
  getCalendarToken: (profileId) =>
    api.get("/api/calendar/token", { params: { profile_id: profileId } }),
  // Daily periods to avoid (§86) — per user, timed at their current location.
  getPeriodsCalendarToken: () => api.get("/api/calendar/periods-token"),

  // ---- Astro-journal (dated life-event diary) ----
  listJournal: (profileId = null) =>
    api.get("/api/journal", { params: { profile_id: profileId || undefined } }),
  createJournal: (payload) => api.post("/api/journal", payload),
  updateJournal: (id, payload) => api.put(`/api/journal/${id}`, payload),
  deleteJournal: (id) => api.delete(`/api/journal/${id}`),

  // ---- Planetary strength (Shadbala + Bhava Bala + Vimsopaka) ----
  getStrength: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/strength", birthDetails, { params: { ayanamsa } }),
  analyzeStrengthAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/strength-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Planetary friendships + house-lord placements + Parivartana ----
  getFriendships: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/friendships", birthDetails, { params: { ayanamsa } }),
  analyzeFriendshipsAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/friendships-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Avasthas (Baladi / Jagradadi / Deeptadi planetary states) ----
  getAvasthas: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/avasthas", birthDetails, { params: { ayanamsa } }),
  analyzeAvasthasAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/avasthas-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Conditional dashas that apply to this chart (BPHS) ----
  getApplicableDashas: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/applicable-dashas", birthDetails, { params: { ayanamsa } }),

  // ---- Remedies (gemstones / mantras / deities per weak planet) ----
  getRemedies: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/remedies", birthDetails, { params: { ayanamsa } }),
  analyzeRemediesAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/remedies-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- KP (Krishnamurti Paddhati) (§16) ----
  // KP always reads on the KP ayanamsa (forced server-side).
  getKpDetails: (birthDetails) => api.post("/api/astrology/kp", birthDetails),
  analyzeKpAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/kp-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        profile_id: opts.profileId,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
      },
      { timeout: 300000 }
    ),
  // KP horary (Prasna) — a number 1-249 fixes the ascendant, cast for now+here.
  getKpHorary: ({ number, date, time, place, latitude, longitude, timezone } = {}) =>
    api.post("/api/astrology/kp-horary", null, {
      params: { number, date, time, place, latitude, longitude, timezone },
    }),
  analyzeKpHoraryAI: (
    { number, question, date, time, place, latitude, longitude, timezone } = {},
    model = {}
  ) =>
    api.post(
      "/api/astrology/kp-horary-analysis",
      {
        number,
        question,
        date,
        time,
        place,
        latitude,
        longitude,
        timezone,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
      },
      { timeout: 300000 }
    ),

  // ---- Jaimini deep-dive (§16) ----
  getJaimini: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/jaimini", birthDetails, { params: { ayanamsa } }),
  analyzeJaiminiAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/jaimini-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        profile_id: opts.profileId,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Chart of the moment / "now" chart (§16) ----
  getNowChart: ({
    place,
    latitude,
    longitude,
    timezone,
    currentTime,
    currentTz,
    ayanamsa = DEFAULT_AYANAMSA,
  } = {}) =>
    api.post("/api/astrology/now-chart", null, {
      params: {
        place,
        latitude,
        longitude,
        timezone,
        current_time: currentTime,
        current_tz: currentTz,
        ayanamsa,
      },
    }),
  analyzeNowChartAI: (
    { place, latitude, longitude, timezone, currentTime, currentTz } = {},
    model = {}
  ) =>
    api.post(
      "/api/astrology/now-chart-analysis",
      {
        place,
        latitude,
        longitude,
        timezone,
        current_time: currentTime,
        current_tz: currentTz,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // New LLM Q&A endpoints
  getLlmProviders: () => api.get("/api/llm/providers"),

  // Catalog of tools the AI astrologer can call (capability disclosure page)
  getAiTools: () => api.get("/api/ai/tools"),

  askQuestion: (birthDetails, question, model = {}) =>
    api.post(
      "/api/astrology/ask",
      {
        birth_details: birthDetails,
        question: question,
        // Back-compat: still send llm_provider; new fields take precedence server-side
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        vargas: model.vargas,
        ayanamsa: model.ayanamsa,
        conversation_id: model.conversationId,
        profile_id: model.profileId,
      },
      // Local models can be slow to load + generate; allow up to 5 minutes
      { timeout: 300000 }
    ),

  // Conversation history (saved Q&A per profile)
  listConversations: (profileId) =>
    api.get("/api/ai/conversations", { params: { profile_id: profileId } }),
  // Unified AI history: every chat + saved reading across all tools (no profile
  // filter → the global History page). Each item carries source/kind/route/context.
  // `digests: true` also folds in the digests that were actually delivered (email
  // / push). They're stored separately under their own retention, so they're
  // opt-in here — only the History page asks for them.
  listHistory: (digests = true) => api.get("/api/ai/conversations", { params: { digests } }),
  getConversation: (id) => api.get(`/api/ai/conversations/${id}`),
  // Lazy-load the full "Behind the scenes" tool results for one saved answer.
  getConversationTrace: (conversationId, traceId) =>
    api.get(`/api/ai/conversations/${conversationId}/traces/${traceId}`),
  deleteConversation: (id) => api.delete(`/api/ai/conversations/${id}`),
  // Thumbs up/down on a specific assistant message (rating: "up"|"down"|null)
  submitFeedback: (conversationId, messageIndex, rating) =>
    api.post(`/api/ai/conversations/${conversationId}/feedback`, {
      message_index: messageIndex,
      rating,
    }),

  // ---- "Did this land?" — outcomes on saved readings (§68.7) ----
  // Thumbs up/down (above) rates the answer; these rate the prediction, later,
  // against what actually happened. `payload.journal` optionally creates the
  // matching astro-journal entry in the same call.
  setReadingOutcome: (readingId, payload) =>
    api.put(`/api/ai/conversations/${readingId}/outcome`, payload),
  clearReadingOutcome: (readingId) => api.delete(`/api/ai/conversations/${readingId}/outcome`),
  listOutcomes: (profileId = null) =>
    api.get("/api/ai/outcomes", { params: { profile_id: profileId || undefined } }),
  // Counts of what the AI can read about this person beyond their chart — the
  // journal and the recorded outcomes. Backs the Ask page's "your data" line.
  getPersonalContext: (profileId = null) =>
    api.get("/api/ai/personal-context", { params: { profile_id: profileId || undefined } }),

  // System health / diagnostics
  getHealth: () => api.get("/health"),

  // Per-user API keys (encrypted server-side; status returns masked values only)
  getApiKeys: () => api.get("/api/user/api-keys"),
  setApiKey: (provider, apiKey) => api.put(`/api/user/api-keys/${provider}`, { api_key: apiKey }),
  deleteApiKey: (provider) => api.delete(`/api/user/api-keys/${provider}`),

  // Cross-device UI preferences (non-secret; e.g. the chosen LLM provider/model)
  getPreferences: () => api.get("/api/user/preferences"),
  putPreferences: (preferences) => api.put("/api/user/preferences", { preferences }),

  // Where the user is NOW — not birth data. `timezone` is an IANA zone name;
  // omit it and the server derives it from the coordinates (which is what the
  // location picker does — its own tz is a float offset, and an offset can't
  // carry DST).
  getCurrentLocation: () => api.get("/api/user/location"),
  putCurrentLocation: ({ place, latitude, longitude, timezone }) =>
    api.put("/api/user/location", { place, latitude, longitude, timezone }),
  // One-click set from the browser's zone: the server geocodes the zone's
  // representative city and verifies it lands back in that zone, so a wrong
  // "Chicago" is a 422 rather than a silently stored wrong answer.
  setCurrentLocationFromZone: (timezone) => api.post("/api/user/location/from-zone", { timezone }),
  deleteCurrentLocation: () => api.delete("/api/user/location"),

  generatePrediction: (birthDetails, predictionType = "general", model = {}) =>
    api.post(
      "/api/astrology/predict",
      {
        birth_details: birthDetails,
        prediction_type: predictionType,
        // Back-compat: still send llm_provider; new fields take precedence server-side
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        vargas: model.vargas,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  analyzeCompatibilityAI: (maleBirthDetails, femaleBirthDetails, model = {}) =>
    api.post(
      "/api/astrology/compatibility-analysis",
      {
        male_details: maleBirthDetails,
        female_details: femaleBirthDetails,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // Neutral AI comparison of two charts (Compare Charts page) — not marriage matching.
  compareChartsAI: (person1, person2, names = {}, model = {}) =>
    api.post(
      "/api/astrology/compare-analysis",
      {
        person1_details: person1,
        person2_details: person2,
        person1_name: names.name1,
        person2_name: names.name2,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // Sarvatobhadra Chakra: the 9×9 grid with today's transits + vedha mapped on it.
  getSarvatobhadra: (
    birthDetails,
    {
      nameNakshatra = null,
      currentDate = null,
      currentTime = null,
      currentTz = null,
      ayanamsa = DEFAULT_AYANAMSA,
    } = {}
  ) =>
    api.post("/api/astrology/sarvatobhadra", birthDetails, {
      params: {
        name_nakshatra: nameNakshatra,
        current_date: currentDate,
        current_time: currentTime,
        current_tz: currentTz,
        ayanamsa,
      },
    }),

  // ---- More chakras (§2.7) ----
  // Kota (the fort — four rings, transit malefics marked) and Tripataki (the
  // rasis on the three-banner diagram). Both share the transit-moment params.
  getKaalaChakra: (
    birthDetails,
    { currentDate = null, currentTime = null, currentTz = null, ayanamsa = DEFAULT_AYANAMSA } = {}
  ) =>
    api.post("/api/astrology/kaala-chakra", birthDetails, {
      params: {
        current_date: currentDate,
        current_time: currentTime,
        current_tz: currentTz,
        ayanamsa,
      },
    }),
  getKotaChakra: (
    birthDetails,
    { currentDate = null, currentTime = null, currentTz = null, ayanamsa = DEFAULT_AYANAMSA } = {}
  ) =>
    api.post("/api/astrology/kota-chakra", birthDetails, {
      params: {
        current_date: currentDate,
        current_time: currentTime,
        current_tz: currentTz,
        ayanamsa,
      },
    }),
  // `basis`: "transit" (the chosen moment) or "annual" (the Varshaphal /
  // solar-return chart for `year` — Tripataki's classical home).
  getTripatakiChakra: (
    birthDetails,
    {
      currentDate = null,
      currentTime = null,
      currentTz = null,
      basis = "transit",
      year = null,
      ayanamsa = DEFAULT_AYANAMSA,
    } = {}
  ) =>
    api.post("/api/astrology/tripataki-chakra", birthDetails, {
      params: {
        current_date: currentDate,
        current_time: currentTime,
        current_tz: currentTz,
        basis,
        year,
        ayanamsa,
      },
    }),

  // Plain-language AI reading of the Sarvatobhadra transit picture.
  analyzeSarvatobhadraAI: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/sarvatobhadra-analysis",
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        name_nakshatra: opts.nameNakshatra,
        current_date: opts.currentDate,
        current_time: opts.currentTime,
        current_tz: opts.currentTz,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // Plain-language AI readings for the Kota / Tripataki chakras (§2.7).
  analyzeChakraAI: (chakra, birthDetails, opts = {}, model = {}) =>
    api.post(
      `/api/astrology/${chakra}-chakra-analysis`,
      {
        birth_details: birthDetails,
        person_name: opts.personName,
        current_date: opts.currentDate,
        current_time: opts.currentTime,
        current_tz: opts.currentTz,
        basis: opts.basis,
        year: opts.year,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // AI reading of the bhava arudhas. `opts.selected` is the list of arudha short
  // codes the user ticked (AL/UL/A2..A11); omitting it lets the server fall back
  // to its default set.
  analyzeArudhas: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/arudha-analysis",
      {
        birth_details: birthDetails,
        profile_id: opts.profileId,
        person_name: opts.personName,
        selected: opts.selected,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // ---- Learn the Chart (AI quiz) ----
  // Generate a quiz grounded in this chart; returns questions without answer keys.
  generateQuiz: (birthDetails, opts = {}, model = {}) =>
    api.post(
      "/api/astrology/quiz/generate",
      {
        birth_details: birthDetails,
        profile_id: opts.profileId,
        topics: opts.topics,
        level: opts.level,
        adaptive: opts.adaptive,
        num_mcq: opts.numMcq,
        num_free: opts.numFree,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  // Grade a quiz session's answers; returns per-question feedback + reasoning.
  gradeQuiz: (sessionId, answers, model = {}) =>
    api.post(
      "/api/astrology/quiz/grade",
      {
        session_id: sessionId,
        answers,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        ayanamsa: model.ayanamsa,
      },
      { timeout: 300000 }
    ),

  getQuizHistory: (profileId = null) =>
    api.get("/api/astrology/quiz/history", { params: { profile_id: profileId } }),

  getQuizStats: (profileId = null) =>
    api.get("/api/astrology/quiz/stats", { params: { profile_id: profileId } }),

  deleteQuiz: (sessionId) => api.delete(`/api/astrology/quiz/${sessionId}`),
};

// --- Ask streaming: a detached job the reader follows (and re-follows) -------
// The server runs each answer as a job that outlives its connection
// (backend/ask_jobs.py). The first frame names the job; every frame carries an
// SSE `id:` sequence number. When the connection drops — Cloudflare cutting an
// idle stream, iOS suspending the tab, a flaky phone network — we re-attach with
// `after=<last seq>` and receive exactly what we missed, so nothing repeats and
// nothing is lost. Heartbeats arrive as SSE comments, which the parser skips.

// Waits between re-attach attempts. Progress resets the count; time spent with
// the tab hidden or the device offline isn't spent on attempts at all.
const ASK_RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000, 15000, 30000, 30000];

const sharedRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// fetch with the bearer token and one silent refresh on 401 — a phone that
// comes back to a job after an hour will usually hold an expired access token.
const authedFetch = async (url, init = {}) => {
  const go = () =>
    fetch(url, {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    });
  const resp = await go();
  if (resp.status !== 401) return resp;
  try {
    await sharedRefresh();
  } catch (e) {
    return resp;
  }
  return go();
};

const abortError = () => {
  const e = new Error("aborted");
  e.name = "AbortError";
  return e;
};

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) return reject(abortError());
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(abortError());
      },
      { once: true }
    );
  });

// Resolve once the page is visible and the device thinks it is online.
const untilReachable = (signal) =>
  new Promise((resolve, reject) => {
    const ok = () =>
      (typeof document === "undefined" || document.visibilityState !== "hidden") &&
      (typeof navigator === "undefined" || navigator.onLine !== false);
    if (ok()) return resolve();
    const check = () => {
      if (!ok()) return;
      cleanup();
      resolve();
    };
    const onAbort = () => {
      cleanup();
      reject(abortError());
    };
    const cleanup = () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("online", check);
      signal.removeEventListener("abort", onAbort);
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("online", check);
    signal.addEventListener("abort", onAbort, { once: true });
  });

// Read an SSE body, calling onFrame(seq, event) per `data:` frame.
const readSse = async (resp, onFrame) => {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const lines = frame.split("\n");
      const dataLine = lines.find((l) => l.startsWith("data:"));
      if (!dataLine) continue; // heartbeat comment
      const idLine = lines.find((l) => l.startsWith("id:"));
      let evt;
      try {
        evt = JSON.parse(dataLine.slice(5).trim());
      } catch (e) {
        continue;
      }
      onFrame(idLine ? parseInt(idLine.slice(3).trim(), 10) || 0 : 0, evt);
    }
  }
};

const httpError = (message, extra = {}) => Object.assign(new Error(message), extra);

/*
 * Drive one Ask job: start it (`request` given) or re-attach to it (`jobId`).
 * Returns stop(opts): stop({ cancelJob: true }) also stops the model on the
 * server (the Stop button); plain stop() only detaches, and the job still
 * finishes and saves its turn — so leaving the page never throws an answer away.
 */
const runAskJob = ({ request = null, jobId = null, after = 0 }, callbacks) => {
  const controller = new AbortController();
  const { signal } = controller;
  const {
    onJob,
    onMeta,
    onToken,
    onDone,
    onError,
    onToolCall,
    onToolResult,
    onNotice,
    onReconnecting,
    onReconnected,
    onGone,
  } = callbacks;
  let lastSeq = after;
  let finished = false;

  const dispatch = (seq, evt) => {
    if (seq) lastSeq = seq;
    if (evt.type === "job") {
      if (jobId !== evt.job_id) announceAiJob();
      jobId = evt.job_id;
      onJob && onJob(jobId);
    } else if (evt.type === "meta") onMeta && onMeta(evt);
    else if (evt.type === "token") onToken && onToken(evt.text);
    else if (evt.type === "tool_call") onToolCall && onToolCall(evt);
    else if (evt.type === "tool_result") onToolResult && onToolResult(evt);
    else if (evt.type === "notice") onNotice && onNotice(evt);
    else if (evt.type === "done") {
      finished = true;
      onDone && onDone(evt);
    } else if (evt.type === "error") {
      finished = true;
      onError && onError(new Error(evt.message));
    } else if (evt.type === "cancelled") {
      finished = true;
      onError && onError(new Error("The answer was stopped."));
    }
  };

  (async () => {
    let attempt = 0;
    let reconnecting = false;
    while (true) {
      const seqBefore = lastSeq;
      try {
        let resp;
        if (!jobId) {
          resp = await authedFetch(`${API_URL}/api/astrology/ask/stream`, {
            method: "POST",
            signal,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(request),
          });
          if (resp.status === 429) {
            const detail =
              (await resp.json().catch(() => null))?.detail ||
              "Rate limit reached. Please slow down.";
            throw httpError(detail, { fatal: true });
          }
        } else {
          resp = await authedFetch(
            `${API_URL}/api/astrology/ask/jobs/${encodeURIComponent(jobId)}?after=${lastSeq}`,
            { signal }
          );
          if (resp.status === 404)
            throw httpError("The answer in progress is gone.", { gone: true });
        }
        if (!resp.ok || !resp.body) {
          const text = await resp.text().catch(() => "");
          throw httpError(text || `Request failed (${resp.status})`, { fatal: true });
        }
        if (reconnecting) {
          reconnecting = false;
          onReconnected && onReconnected();
        }
        await readSse(resp, dispatch);
        if (finished) return;
        // The body ended without a verdict: the connection was cut mid-answer.
        throw httpError("The connection closed before the answer finished.");
      } catch (err) {
        if (finished) return;
        if (err.name === "AbortError" || signal.aborted) return;
        if (err.gone) {
          if (onGone) onGone();
          else onError && onError(err);
          return;
        }
        // Without a job id there is nothing to re-attach to.
        if (err.fatal || !jobId) {
          onError && onError(err);
          return;
        }
        if (lastSeq > seqBefore) attempt = 0;
        if (attempt >= ASK_RETRY_DELAYS.length) {
          onError && onError(err);
          return;
        }
        reconnecting = true;
        onReconnecting && onReconnecting(attempt + 1);
        try {
          await sleep(ASK_RETRY_DELAYS[attempt++], signal);
          await untilReachable(signal);
        } catch (e) {
          return; // stopped while waiting
        }
      }
    }
  })();

  return ({ cancelJob = false } = {}) => {
    controller.abort();
    if (cancelJob && jobId && !finished) {
      authedFetch(`${API_URL}/api/astrology/ask/jobs/${encodeURIComponent(jobId)}`, {
        method: "DELETE",
      }).catch(() => {});
    }
  };
};

/**
 * Stream an AI answer over SSE (fetch + ReadableStream — axios can't stream in
 * the browser). Calls callbacks as events arrive and re-attaches by itself when
 * the connection drops. Returns stop(opts) — see runAskJob.
 *   callbacks: { onJob, onMeta, onToken, onDone, onError, onToolCall,
 *                onToolResult, onNotice, onReconnecting, onReconnected, onGone }
 */
export const streamAskQuestion = (birthDetails, question, model = {}, callbacks = {}) =>
  runAskJob(
    {
      request: {
        birth_details: birthDetails,
        question,
        llm_provider: model.legacyProvider || "qwen",
        provider_type: model.providerType,
        model: model.model,
        base_url: model.baseUrl,
        api_key: model.apiKey,
        max_tokens: model.maxTokens || undefined,
        vargas: model.vargas,
        sections: model.sections,
        ayanamsa: model.ayanamsa,
        mode: model.mode,
        source: model.source,
        conversation_id: model.conversationId,
        profile_id: model.profileId,
        regenerate: model.regenerate || false,
      },
    },
    callbacks
  );

/** Re-attach to a job after the page itself was lost (iOS killed the tab):
 * replays the whole answer from the start. `onGone` fires if the server no
 * longer has it — the saved conversation is then the place to look. */
export const resumeAskStream = (jobId, callbacks = {}) => runAskJob({ jobId, after: 0 }, callbacks);

// What the AI is working on for this user (backend/ai_activity.py) — read by
// the header pill, History's "In progress" and the Ask page's resume.
export const aiActivityService = {
  list: () => api.get("/api/ai/activity", { timeout: 15000 }),
  markSeen: (jobId) => api.post(`/api/ai/activity/${encodeURIComponent(jobId)}/seen`),
};

// The newcomer's first minute (§76). `browserTz` is the viewer's UTC offset —
// it decides which dasha period is "now" for someone with no stored location.
const browserTz = () => -new Date().getTimezoneOffset() / 60;

export const onboardingService = {
  // "Your chart in plain words" for the selected profile (signed in).
  firstLook: (birthDetails, ayanamsa = DEFAULT_AYANAMSA) =>
    api.post("/api/astrology/first-look", {
      birth_details: birthDetails,
      ayanamsa,
      current_tz: browserTz(),
    }),
  // The landing page's preview — no account, nothing stored server-side.
  previewFirstLook: (birthDetails) =>
    api.post("/api/public/first-look", { birth_details: birthDetails, current_tz: browserTz() }),
  // Anonymous funnel beacon (landing page opened). Never throws.
  beacon: (event) => api.post("/api/public/funnel", { event }).catch(() => {}),
  welcomeDone: () => api.post("/api/onboarding/welcome-done").catch(() => {}),
};

export default api;
